import React, { useState, useRef, createElement } from 'react';
import { Package, Plus, ShoppingBag, DollarSign, Eye, EyeOff, Edit2, Trash2, Copy, ExternalLink, Megaphone, MoreVertical, Star, FileText, Video, Link as LinkIcon, X, CheckCircle2, AlertCircle, GripVertical, AlignLeft, UploadCloud, ArrowRight, Clock } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Input } from '../ui/Input';
import { useClickOutside } from '../layout/header/useClickOutside';
import { initialMarketplaceProducts, initialMarketplaceOrders, initialMarketplacePayouts } from '../../data/marketplaceMockData';
interface MarketplaceTabProps {
  showToast: (msg: string) => void;
  setActiveTab?: (tab: string) => void;
}
type TabType = 'products' | 'create' | 'sales' | 'balance';
// Types for the product builder
type BlockType = 'text' | 'video' | 'file' | 'link' | 'module';
interface ContentBlock {
  id: string;
  type: BlockType;
  title: string;
  content?: string;
  url?: string;
  fileSize?: string;
}
export function MarketplaceTab({
  showToast,
  setActiveTab: setDashboardTab
}: MarketplaceTabProps) {
  const [activeTab, setActiveTab] = useState<TabType>('products');
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  // Mock Data
  const [myProducts, setMyProducts] = useState(initialMarketplaceProducts.filter(p => p.authorId === 'AST-1'));
  const myOrders = initialMarketplaceOrders.filter(o => o.sellerId === 'AST-1');
  const myPayouts = initialMarketplacePayouts.filter(p => p.sellerId === 'AST-1');
  // Editor State
  const [productForm, setProductForm] = useState({
    title: '',
    type: 'курс',
    price: '',
    shortDesc: '',
    accessType: 'auto'
  });
  const [contentBlocks, setContentBlocks] = useState<ContentBlock[]>([]);
  const [coverImage, setCoverImage] = useState<string | null>(null);
  const [targetAudience, setTargetAudience] = useState('');
  const [whatUserGets, setWhatUserGets] = useState<string[]>(['']);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useClickOutside(dropdownRef, () => setOpenDropdownId(null));
  // Handlers
  const handleCreateNew = () => {
    setEditingProductId(null);
    setProductForm({
      title: '',
      type: 'курс',
      price: '',
      shortDesc: '',
      accessType: 'auto'
    });
    setContentBlocks([]);
    setCoverImage(null);
    setTargetAudience('');
    setWhatUserGets(['']);
    setActiveTab('create');
  };
  const handleEdit = (id: string) => {
    const product = myProducts.find(p => p.id === id);
    if (product) {
      setEditingProductId(id);
      setProductForm({
        title: product.title,
        type: product.type,
        price: product.priceNum.toString(),
        shortDesc: product.description,
        accessType: 'auto'
      });
      // Mock blocks based on existing materials
      setContentBlocks(product.materials.map((m, i) => ({
        id: `block-${i}`,
        type: m.type === 'video' ? 'video' : m.type === 'pdf' ? 'file' : 'link',
        title: m.name,
        fileSize: m.size
      })));
      setCoverImage(null);
      setTargetAudience(product.targetAudience || '');
      setWhatUserGets(product.whatUserGets?.length ? product.whatUserGets : ['']);
      setActiveTab('create');
    }
  };
  const handleDelete = (id: string) => {
    setMyProducts(myProducts.filter(p => p.id !== id));
    setShowDeleteConfirm(null);
    showToast('Продукт успешно удален');
    // Log to admin
    console.log(`[Admin Log] Product deleted: ${id}`);
  };
  const handleDuplicate = (id: string) => {
    const product = myProducts.find(p => p.id === id);
    if (product) {
      const newProduct = {
        ...product,
        id: `PROD-${Math.floor(Math.random() * 10000)}`,
        title: `${product.title} (Копия)`,
        status: 'draft' as const,
        salesCount: 0,
        revenue: '0 ₽',
        rating: 0,
        reviewsCount: 0
      };
      setMyProducts([newProduct, ...myProducts]);
      showToast('Продукт дублирован');
      setOpenDropdownId(null);
    }
  };
  const handleToggleVisibility = (id: string) => {
    setMyProducts(myProducts.map(p => {
      if (p.id === id) {
        const newStatus = p.status === 'hidden' ? 'active' : 'hidden';
        showToast(newStatus === 'active' ? 'Продукт опубликован' : 'Продукт скрыт');
        return {
          ...p,
          status: newStatus
        };
      }
      return p;
    }));
    setOpenDropdownId(null);
  };
  const handleSaveProduct = (status: 'draft' | 'moderation') => {
    if (!productForm.title || !productForm.price) {
      showToast('Заполните название и цену');
      return;
    }
    const newProduct = {
      id: editingProductId || `PROD-${Math.floor(Math.random() * 10000)}`,
      title: productForm.title,
      description: productForm.shortDesc,
      type: productForm.type as any,
      category: 'Обучение',
      price: `${parseInt(productForm.price).toLocaleString('ru-RU')} ₽`,
      priceNum: parseInt(productForm.price),
      authorId: 'AST-1',
      authorName: 'Александр Ведов',
      authorAvatar: 'https://i.pravatar.cc/150?u=ast-1',
      status: status,
      salesCount: 0,
      revenue: '0 ₽',
      rating: 0,
      reviewsCount: 0,
      coverColor: 'bg-indigo-100',
      createdAt: new Date().toISOString().split('T')[0],
      updatedAt: new Date().toISOString().split('T')[0],
      materials: contentBlocks.map(b => ({
        type: b.type === 'file' ? 'pdf' : b.type,
        name: b.title,
        size: b.fileSize
      })),
      targetAudience: targetAudience,
      whatUserGets: whatUserGets.filter(Boolean),
      language: 'Русский',
      items: []
    };
    if (editingProductId) {
      setMyProducts(myProducts.map(p => p.id === editingProductId ? {
        ...p,
        ...newProduct,
        salesCount: p.salesCount,
        revenue: p.revenue,
        rating: p.rating,
        reviewsCount: p.reviewsCount
      } : p));
    } else {
      setMyProducts([newProduct, ...myProducts]);
    }
    showToast(status === 'draft' ? 'Черновик сохранен' : 'Отправлено на модерацию');
    // Log to admin
    console.log(`[Admin Log] Product ${editingProductId ? 'updated' : 'created'}: ${newProduct.title}, Status: ${status}`);
    setActiveTab('products');
  };
  // Builder Handlers
  const addBlock = (type: BlockType) => {
    setContentBlocks([...contentBlocks, {
      id: `block-${Date.now()}`,
      type,
      title: type === 'module' ? 'Новый модуль' : ''
    }]);
  };
  const updateBlock = (id: string, updates: Partial<ContentBlock>) => {
    setContentBlocks(contentBlocks.map(b => b.id === id ? {
      ...b,
      ...updates
    } : b));
  };
  const removeBlock = (id: string) => {
    setContentBlocks(contentBlocks.filter(b => b.id !== id));
  };
  const moveBlock = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0 || direction === 'down' && index === contentBlocks.length - 1) return;
    const newBlocks = [...contentBlocks];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    [newBlocks[index], newBlocks[swapIndex]] = [newBlocks[swapIndex], newBlocks[index]];
    setContentBlocks(newBlocks);
  };
  // Renderers
  const renderTopStats = () => {
    const totalRevenueNum = myOrders.reduce((sum, order) => sum + order.priceNum, 0);
    return <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6" data-id="element-13080">
        <Card padding="sm" hoverable className={`border-2 transition-colors ${activeTab === 'products' ? 'border-indigo-500 bg-indigo-50/30' : 'border-transparent hover:border-slate-200'}`} onClick={() => setActiveTab('products')} data-id="element-13081">
          <div className="flex flex-col" data-id="element-13082">
            <div className="flex items-center gap-2 text-slate-500 mb-2" data-id="element-13083">
              <Package className="w-4 h-4" data-id="element-13084" />
              <span className="text-xs font-semibold uppercase tracking-wider" data-id="element-13085">
                Мои продукты
              </span>
            </div>
            <div className="text-2xl font-bold text-slate-900" data-id="element-13086">
              {myProducts.length}
            </div>
          </div>
        </Card>

        <Card padding="sm" hoverable className={`border-2 transition-colors ${activeTab === 'sales' ? 'border-emerald-500 bg-emerald-50/30' : 'border-transparent hover:border-slate-200'}`} onClick={() => setActiveTab('sales')} data-id="element-13087">
          <div className="flex flex-col" data-id="element-13088">
            <div className="flex items-center gap-2 text-slate-500 mb-2" data-id="element-13089">
              <ShoppingBag className="w-4 h-4" data-id="element-13090" />
              <span className="text-xs font-semibold uppercase tracking-wider" data-id="element-13091">
                Продажи
              </span>
            </div>
            <div className="text-2xl font-bold text-slate-900" data-id="element-13092">
              {myOrders.length}
            </div>
          </div>
        </Card>

        <Card padding="sm" className="border-transparent" data-id="element-13093">
          <div className="flex flex-col" data-id="element-13094">
            <div className="flex items-center gap-2 text-slate-500 mb-2" data-id="element-13095">
              <Star className="w-4 h-4" data-id="element-13096" />
              <span className="text-xs font-semibold uppercase tracking-wider" data-id="element-13097">
                Общий доход
              </span>
            </div>
            <div className="text-2xl font-bold text-slate-900" data-id="element-13098">
              {totalRevenueNum.toLocaleString('ru-RU')} ₽
            </div>
          </div>
        </Card>

        <Card padding="sm" hoverable className={`border-2 transition-colors ${activeTab === 'balance' ? 'border-sky-500 bg-sky-50/30' : 'border-transparent hover:border-slate-200'}`} onClick={() => setActiveTab('balance')} data-id="element-13099">
          <div className="flex flex-col" data-id="element-13100">
            <div className="flex items-center gap-2 text-slate-500 mb-2" data-id="element-13101">
              <DollarSign className="w-4 h-4" data-id="element-13102" />
              <span className="text-xs font-semibold uppercase tracking-wider" data-id="element-13103">
                Баланс
              </span>
            </div>
            <div className="text-2xl font-bold text-sky-600" data-id="element-13104">45 000 ₽</div>
          </div>
        </Card>
      </div>;
  };
  const renderProducts = () => <div className="space-y-4 animate-in fade-in duration-300" data-id="element-13105">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6" data-id="element-13106">
        <h3 className="text-lg font-bold text-slate-900" data-id="element-13107">
          Управление продуктами
        </h3>
        <Button icon={<Plus className="w-4 h-4" data-id="element-13109" />} onClick={handleCreateNew} data-id="element-13108">
          Создать продукт
        </Button>
      </div>

      <div className="flex flex-col gap-3" data-id="element-13110">
        {myProducts.map(product => <Card key={product.id} padding="sm" className="relative group" data-id="element-13111">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4" data-id="element-13112">
              {/* Left: Status + Info */}
              <div className="flex-1 min-w-0" data-id="element-13113">
                <div className="flex items-center gap-2 mb-1.5" data-id="element-13114">
                  <Badge variant={product.status === 'active' ? 'success' : product.status === 'moderation' ? 'warning' : product.status === 'hidden' ? 'neutral' : 'default'} data-id="element-13115">
                    {product.status === 'active' ? 'Опубликован' : product.status === 'moderation' ? 'На проверке' : product.status === 'hidden' ? 'Скрыт' : 'Черновик'}
                  </Badge>
                  <span className="text-xs text-slate-400 uppercase tracking-wider font-semibold" data-id="element-13116">
                    {product.type}
                  </span>
                </div>
                <h4 className="font-bold text-slate-900 text-base leading-tight truncate" title={product.title} data-id="element-13117">
                  {product.title}
                </h4>
                <div className="text-lg font-bold text-indigo-600 mt-0.5" data-id="element-13118">
                  {product.price}
                </div>
              </div>

              {/* Middle: Stats */}
              <div className="flex items-center gap-4 sm:gap-6 shrink-0" data-id="element-13119">
                <div className="text-center sm:text-left" data-id="element-13120">
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold" data-id="element-13121">
                    Продажи
                  </div>
                  <div className="font-bold text-slate-900" data-id="element-13122">
                    {product.salesCount}
                  </div>
                </div>
                <div className="text-center sm:text-left" data-id="element-13123">
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold" data-id="element-13124">
                    Доход
                  </div>
                  <div className="font-bold text-emerald-600" data-id="element-13125">
                    {product.revenue}
                  </div>
                </div>
              </div>

              {/* Right: Actions */}
              <div className="flex items-center gap-2 shrink-0" data-id="element-13126">
                <Button variant="primary" size="sm" onClick={() => handleEdit(product.id)} data-id="element-13127">
                  Редактировать
                </Button>
                <Button variant="outline" size="sm" className="px-2.5 text-rose-500 hover:bg-rose-50 hover:border-rose-200" onClick={() => setShowDeleteConfirm(product.id)} data-id="element-13128">
                  <Trash2 className="w-4 h-4" data-id="element-13129" />
                </Button>

                {/* Dropdown Menu */}
                <div className="relative" ref={openDropdownId === product.id ? dropdownRef : null} data-id="element-13130">
                  <button onClick={() => setOpenDropdownId(openDropdownId === product.id ? null : product.id)} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-13131">
                    <MoreVertical className="w-5 h-5" data-id="element-13132" />
                  </button>

                  {openDropdownId === product.id && <div className="absolute right-0 mt-1 w-48 bg-white rounded-xl shadow-lg border border-slate-100 py-1 overflow-hidden animate-in fade-in slide-in-from-top-2 z-30" data-id="element-13133">
                      <button onClick={() => handleDuplicate(product.id)} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50" data-id="element-13134">
                        <Copy className="w-4 h-4 text-slate-400" data-id="element-13135" /> Дублировать
                      </button>
                      <button onClick={() => handleToggleVisibility(product.id)} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50" data-id="element-13136">
                        {product.status === 'hidden' ? <>
                            <Eye className="w-4 h-4 text-slate-400" data-id="element-13137" /> Показать
                          </> : <>
                            <EyeOff className="w-4 h-4 text-slate-400" data-id="element-13138" /> Скрыть
                          </>}
                      </button>
                      <button onClick={() => {
                  showToast('Открыто в новой вкладке');
                  setOpenDropdownId(null);
                }} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50" data-id="element-13139">
                        <ExternalLink className="w-4 h-4 text-slate-400" data-id="element-13140" />{' '}
                        Открыть как юзер
                      </button>
                      <button onClick={() => {
                  if (setDashboardTab) setDashboardTab('promotion');else showToast('Переход в рекламный кабинет');
                  setOpenDropdownId(null);
                }} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-indigo-600 hover:bg-indigo-50 border-t border-slate-100" data-id="element-13141">
                        <Megaphone className="w-4 h-4" data-id="element-13142" /> Продвигать
                      </button>
                    </div>}
                </div>
              </div>
            </div>
          </Card>)}

        {myProducts.length === 0 && <div className="col-span-full py-12 text-center bg-slate-50 rounded-2xl border border-slate-200 border-dashed" data-id="element-13143">
            <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" data-id="element-13144" />
            <h3 className="text-lg font-medium text-slate-900 mb-1" data-id="element-13145">
              У вас пока нет продуктов
            </h3>
            <p className="text-slate-500 mb-4" data-id="element-13146">
              Создайте свой первый курс, гайд или вебинар
            </p>
            <Button icon={<Plus className="w-4 h-4" data-id="element-13148" />} onClick={handleCreateNew} data-id="element-13147">
              Создать продукт
            </Button>
          </div>}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm" data-id="element-13149">
          <Card className="w-full max-w-sm shadow-2xl animate-in zoom-in-95 duration-200" data-id="element-13150">
            <div className="text-center" data-id="element-13151">
              <div className="w-12 h-12 rounded-full bg-rose-100 flex items-center justify-center mx-auto mb-4" data-id="element-13152">
                <AlertCircle className="w-6 h-6 text-rose-600" data-id="element-13153" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-13154">
                Удалить продукт?
              </h3>
              <p className="text-sm text-slate-500 mb-6" data-id="element-13155">
                Это действие нельзя отменить. Продукт будет навсегда удален из
                каталога.
              </p>
              <div className="flex gap-3" data-id="element-13156">
                <Button variant="outline" className="flex-1" onClick={() => setShowDeleteConfirm(null)} data-id="element-13157">
                  Отмена
                </Button>
                <Button variant="primary" className="flex-1 bg-rose-600 hover:bg-rose-700 border-rose-600" onClick={() => handleDelete(showDeleteConfirm)} data-id="element-13158">
                  Удалить
                </Button>
              </div>
            </div>
          </Card>
        </div>}
    </div>;
  const renderProductEditor = () => <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300 max-w-4xl mx-auto pb-20" data-id="element-13159">
      <div className="flex items-center justify-between mb-6" data-id="element-13160">
        <div className="flex items-center gap-3" data-id="element-13161">
          <button onClick={() => setActiveTab('products')} className="p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-500" data-id="element-13162">
            <ArrowRight className="w-5 h-5 rotate-180" data-id="element-13163" />
          </button>
          <h2 className="text-2xl font-bold text-slate-900" data-id="element-13164">
            {editingProductId ? 'Редактирование продукта' : 'Новый продукт'}
          </h2>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-13165">
        {/* Left Column: Main Info & Content */}
        <div className="lg:col-span-2 space-y-6" data-id="element-13166">
          {/* Basic Info */}
          <Card padding="lg" className="space-y-5" data-id="element-13167">
            <h3 className="text-lg font-bold text-slate-900 border-b border-slate-100 pb-3" data-id="element-13168">
              Основная информация
            </h3>

            <div className="grid grid-cols-2 gap-4" data-id="element-13169">
              <div className="col-span-2" data-id="element-13170">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-13171">
                  Название *
                </label>
                <Input placeholder="Например: Гайд по транзитам Сатурна" value={productForm.title} onChange={e => setProductForm({
                ...productForm,
                title: e.target.value
              })} data-id="element-13172" />
              </div>

              <div data-id="element-13173">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-13174">
                  Тип продукта
                </label>
                <select className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors" value={productForm.type} onChange={e => setProductForm({
                ...productForm,
                type: e.target.value
              })} data-id="element-13175">
                  <option value="курс" data-id="element-13176">Курс</option>
                  <option value="pdf" data-id="element-13177">Файл / PDF</option>
                  <option value="вебинар" data-id="element-13178">Вебинар</option>
                  <option value="закрытый_клуб" data-id="element-13179">Подписка (Клуб)</option>
                </select>
              </div>

              <div data-id="element-13180">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-13181">
                  Цена (₽) *
                </label>
                <Input type="number" placeholder="0" value={productForm.price} onChange={e => setProductForm({
                ...productForm,
                price: e.target.value
              })} data-id="element-13182" />
              </div>

              <div className="col-span-2" data-id="element-13183">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-13184">
                  Краткое описание
                </label>
                <textarea className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors resize-y" rows={3} placeholder="О чем этот продукт? Какую пользу он принесет?" value={productForm.shortDesc} onChange={e => setProductForm({
                ...productForm,
                shortDesc: e.target.value
              })} data-id="element-13185" />
              </div>

              {/* Cover Image */}
              <div className="col-span-2" data-id="element-13186">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-13187">
                  Обложка
                </label>
                {coverImage ? <div className="relative rounded-xl overflow-hidden border border-slate-200 h-40 bg-slate-100" data-id="element-13188">
                    <img src={coverImage} alt="Обложка" className="w-full h-full object-cover" data-id="element-13189" />
                    <button onClick={() => setCoverImage(null)} className="absolute top-2 right-2 p-1.5 bg-white/90 rounded-lg text-slate-600 hover:text-rose-600 shadow-sm" data-id="element-13190">
                      <X className="w-4 h-4" data-id="element-13191" />
                    </button>
                  </div> : <button onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = 'image/*';
                input.onchange = e => {
                  const file = (e.target as HTMLInputElement).files?.[0];
                  if (file) {
                    const url = URL.createObjectURL(file);
                    setCoverImage(url);
                    showToast('Обложка загружена ✓');
                  }
                };
                input.click();
              }} className="w-full h-32 border-2 border-dashed border-slate-200 rounded-xl flex flex-col items-center justify-center gap-2 text-slate-400 hover:border-indigo-300 hover:text-indigo-500 transition-colors" data-id="element-13192">
                    <UploadCloud className="w-6 h-6" data-id="element-13193" />
                    <span className="text-sm" data-id="element-13194">
                      Загрузить обложку (рекомендуется 1200×630)
                    </span>
                  </button>}
              </div>

              {/* Target Audience */}
              <div className="col-span-2" data-id="element-13195">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-13196">
                  Для кого этот продукт
                </label>
                <Input placeholder="Например: Начинающие астрологи, которые хотят освоить транзиты" value={targetAudience} onChange={e => setTargetAudience(e.target.value)} data-id="element-13197" />
              </div>

              {/* What User Gets */}
              <div className="col-span-2" data-id="element-13198">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-13199">
                  Что получит клиент
                </label>
                <div className="space-y-2" data-id="element-13200">
                  {whatUserGets.map((item, idx) => <div key={idx} className="flex items-center gap-2" data-id="element-13201">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" data-id="element-13202" />
                      <Input placeholder={`Пункт ${idx + 1}, например: 10 видеоуроков`} value={item} onChange={e => {
                    const updated = [...whatUserGets];
                    updated[idx] = e.target.value;
                    setWhatUserGets(updated);
                  }} className="flex-1" data-id="element-13203" />
                      {whatUserGets.length > 1 && <button onClick={() => setWhatUserGets(whatUserGets.filter((_, i) => i !== idx))} className="p-1.5 text-slate-400 hover:text-rose-500 rounded-lg" data-id="element-13204">
                          <Trash2 className="w-3.5 h-3.5" data-id="element-13205" />
                        </button>}
                    </div>)}
                  <button onClick={() => setWhatUserGets([...whatUserGets, ''])} className="text-xs text-indigo-600 font-medium hover:underline flex items-center gap-1" data-id="element-13206">
                    <Plus className="w-3.5 h-3.5" data-id="element-13207" /> Добавить пункт
                  </button>
                </div>
              </div>
            </div>
          </Card>

          {/* Content Builder */}
          <Card padding="lg" className="space-y-5" data-id="element-13208">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3" data-id="element-13209">
              <h3 className="text-lg font-bold text-slate-900" data-id="element-13210">
                Содержимое (Материалы)
              </h3>
            </div>

            <div className="space-y-3" data-id="element-13211">
              {contentBlocks.map((block, index) => <div key={block.id} className="flex gap-3 bg-slate-50 border border-slate-200 rounded-xl p-3 group" data-id="element-13212">
                  <div className="flex flex-col gap-1 items-center justify-center text-slate-300" data-id="element-13213">
                    <button onClick={() => moveBlock(index, 'up')} className="hover:text-slate-600 disabled:opacity-30" disabled={index === 0} data-id="element-13214">
                      <GripVertical className="w-4 h-4" data-id="element-13215" />
                    </button>
                    <button onClick={() => moveBlock(index, 'down')} className="hover:text-slate-600 disabled:opacity-30" disabled={index === contentBlocks.length - 1} data-id="element-13216">
                      <GripVertical className="w-4 h-4" data-id="element-13217" />
                    </button>
                  </div>

                  <div className="flex-1 space-y-3" data-id="element-13218">
                    <div className="flex items-center gap-2" data-id="element-13219">
                      {block.type === 'module' && <Badge variant="neutral" data-id="element-13220">Модуль</Badge>}
                      {block.type === 'video' && <Badge variant="pro" data-id="element-13221">
                          <Video className="w-3 h-3 mr-1" data-id="element-13222" /> Видео
                        </Badge>}
                      {block.type === 'file' && <Badge variant="error" data-id="element-13223">
                          <FileText className="w-3 h-3 mr-1" data-id="element-13224" /> Файл
                        </Badge>}
                      {block.type === 'link' && <Badge variant="success" data-id="element-13225">
                          <LinkIcon className="w-3 h-3 mr-1" data-id="element-13226" /> Ссылка
                        </Badge>}
                      {block.type === 'text' && <Badge variant="default" data-id="element-13227">
                          <AlignLeft className="w-3 h-3 mr-1" data-id="element-13228" /> Текст
                        </Badge>}

                      <input type="text" placeholder={block.type === 'module' ? 'Название модуля' : 'Название материала'} className="flex-1 bg-transparent border-b border-transparent hover:border-slate-300 focus:border-indigo-500 focus:outline-none px-1 py-0.5 text-sm font-medium text-slate-900 transition-colors" value={block.title} onChange={e => updateBlock(block.id, {
                    title: e.target.value
                  })} data-id="element-13229" />
                    </div>

                    {block.type === 'text' && <textarea className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500" rows={2} placeholder="Текст урока или описание..." value={block.content || ''} onChange={e => updateBlock(block.id, {
                  content: e.target.value
                })} data-id="element-13230" />}

                    {(block.type === 'video' || block.type === 'link') && <Input placeholder="Ссылка (YouTube, Google Drive, Vimeo...)" value={block.url || ''} onChange={e => updateBlock(block.id, {
                  url: e.target.value
                })} className="bg-white" data-id="element-13231" />}

                    {block.type === 'file' && <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3" data-id="element-13232">
                        <Button variant="outline" size="sm" icon={<UploadCloud className="w-4 h-4" data-id="element-13234" />} onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = '.pdf,.doc,.docx,.xls,.xlsx,.zip,.rar';
                    input.onchange = e => {
                      const file = (e.target as HTMLInputElement).files?.[0];
                      if (file) {
                        updateBlock(block.id, {
                          title: block.title || file.name,
                          fileSize: `${(file.size / 1024 / 1024).toFixed(1)} MB`
                        });
                        showToast(`Файл "${file.name}" загружен ✓`);
                      }
                    };
                    input.click();
                  }} data-id="element-13233">
                          Загрузить файл
                        </Button>
                        {block.fileSize && <span className="text-xs text-emerald-600 font-medium" data-id="element-13235">
                            ✓ {block.fileSize}
                          </span>}
                        <span className="text-xs text-slate-500" data-id="element-13236">или</span>
                        <Input placeholder="Внешняя ссылка на файл" value={block.url || ''} onChange={e => updateBlock(block.id, {
                    url: e.target.value
                  })} className="flex-1 bg-white" data-id="element-13237" />
                      </div>}
                  </div>

                  <button onClick={() => removeBlock(block.id)} className="p-2 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-colors self-start sm:opacity-0 sm:group-hover:opacity-100" data-id="element-13238">
                    <Trash2 className="w-4 h-4" data-id="element-13239" />
                  </button>
                </div>)}

              {contentBlocks.length === 0 && <div className="text-center py-8 text-slate-500 text-sm border-2 border-dashed border-slate-200 rounded-xl" data-id="element-13240">
                  Добавьте материалы, которые получит клиент после оплаты
                </div>}
            </div>

            <div className="flex flex-wrap gap-2 pt-2" data-id="element-13241">
              <Button variant="outline" size="sm" icon={<Plus className="w-4 h-4" data-id="element-13243" />} onClick={() => addBlock('module')} data-id="element-13242">
                Модуль
              </Button>
              <Button variant="outline" size="sm" icon={<Video className="w-4 h-4" data-id="element-13245" />} onClick={() => addBlock('video')} data-id="element-13244">
                Видео
              </Button>
              <Button variant="outline" size="sm" icon={<FileText className="w-4 h-4" data-id="element-13247" />} onClick={() => addBlock('file')} data-id="element-13246">
                Файл
              </Button>
              <Button variant="outline" size="sm" icon={<LinkIcon className="w-4 h-4" data-id="element-13249" />} onClick={() => addBlock('link')} data-id="element-13248">
                Ссылка
              </Button>
              <Button variant="outline" size="sm" icon={<AlignLeft className="w-4 h-4" data-id="element-13251" />} onClick={() => addBlock('text')} data-id="element-13250">
                Текст
              </Button>
            </div>
          </Card>
        </div>

        {/* Right Column: Settings & Actions */}
        <div className="space-y-6" data-id="element-13252">
          <Card padding="lg" className="space-y-5" data-id="element-13253">
            <h3 className="text-lg font-bold text-slate-900 border-b border-slate-100 pb-3" data-id="element-13254">
              Настройки доступа
            </h3>

            <div className="space-y-3" data-id="element-13255">
              <label className="flex items-start gap-3 p-3 border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-50 transition-colors" data-id="element-13256">
                <input type="radio" name="accessType" value="auto" checked={productForm.accessType === 'auto'} onChange={e => setProductForm({
                ...productForm,
                accessType: e.target.value
              })} className="mt-1 text-indigo-600 focus:ring-indigo-500" data-id="element-13257" />
                <div data-id="element-13258">
                  <div className="font-medium text-slate-900 text-sm" data-id="element-13259">
                    Автоматически
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5" data-id="element-13260">
                    Клиент получает доступ сразу после успешной оплаты
                  </div>
                </div>
              </label>

              <label className="flex items-start gap-3 p-3 border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-50 transition-colors" data-id="element-13261">
                <input type="radio" name="accessType" value="manual" checked={productForm.accessType === 'manual'} onChange={e => setProductForm({
                ...productForm,
                accessType: e.target.value
              })} className="mt-1 text-indigo-600 focus:ring-indigo-500" data-id="element-13262" />
                <div data-id="element-13263">
                  <div className="font-medium text-slate-900 text-sm" data-id="element-13264">
                    Вручную
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5" data-id="element-13265">
                    Вы сами открываете доступ или отправляете материалы
                  </div>
                </div>
              </label>
            </div>
          </Card>

          {/* Sticky Actions */}
          <div className="sticky top-24 space-y-3" data-id="element-13266">
            <Button variant="primary" size="lg" fullWidth onClick={() => handleSaveProduct('moderation')} data-id="element-13267">
              Отправить на публикацию
            </Button>
            <Button variant="outline" size="lg" fullWidth onClick={() => handleSaveProduct('draft')} data-id="element-13268">
              Сохранить как черновик
            </Button>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-4" data-id="element-13269">
              <div className="flex gap-2 text-amber-800" data-id="element-13270">
                <AlertCircle className="w-5 h-5 shrink-0" data-id="element-13271" />
                <div className="text-xs leading-relaxed" data-id="element-13272">
                  <strong data-id="element-13273">Модерация:</strong> После отправки на публикацию
                  продукт будет проверен администратором. Обычно это занимает до
                  2 часов.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
  const renderSales = () => <div className="space-y-4 animate-in fade-in duration-300" data-id="element-13274">
      <div className="flex justify-between items-center mb-4" data-id="element-13275">
        <h3 className="text-lg font-bold text-slate-900" data-id="element-13276">Недавние продажи</h3>
      </div>

      <Card padding="none" className="overflow-hidden" data-id="element-13277">
        <div className="overflow-x-auto" data-id="element-13278">
          <table className="w-full text-left text-sm" data-id="element-13279">
            <thead className="bg-slate-50 text-slate-500 border-b border-slate-200" data-id="element-13280">
              <tr data-id="element-13281">
                <th className="px-4 py-3 font-medium" data-id="element-13282">Дата</th>
                <th className="px-4 py-3 font-medium" data-id="element-13283">Продукт</th>
                <th className="px-4 py-3 font-medium" data-id="element-13284">Покупатель</th>
                <th className="px-4 py-3 font-medium" data-id="element-13285">Сумма</th>
                <th className="px-4 py-3 font-medium" data-id="element-13286">Доступ</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-13287">
              {myOrders.map(order => <tr key={order.id} className="hover:bg-slate-50" data-id="element-13288">
                  <td className="px-4 py-3 text-slate-600" data-id="element-13289">
                    {order.createdAt.split(',')[0]}
                  </td>
                  <td className="px-4 py-3" data-id="element-13290">
                    <div className="font-medium text-slate-900" data-id="element-13291">
                      {order.productTitle}
                    </div>
                    <div className="text-xs text-slate-500 capitalize" data-id="element-13292">
                      {order.productType}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600" data-id="element-13293">
                    {order.buyerName}
                  </td>
                  <td className="px-4 py-3 font-bold text-emerald-600" data-id="element-13294">
                    {order.price}
                  </td>
                  <td className="px-4 py-3" data-id="element-13295">
                    <Badge variant={order.accessStatus === 'granted' ? 'success' : 'warning'} data-id="element-13296">
                      {order.accessStatus === 'granted' ? 'Открыт' : 'Ожидание'}
                    </Badge>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </Card>
    </div>;
  const renderBalance = () => <div className="space-y-6 animate-in fade-in duration-300" data-id="element-13297">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-13298">
        <Card padding="lg" className="bg-indigo-50/50 border-indigo-100 flex flex-col items-center text-center" data-id="element-13299">
          <div className="text-sm text-slate-500 mb-2 font-medium" data-id="element-13300">
            Доступно к выводу
          </div>
          <div className="text-4xl font-bold text-indigo-700 mb-6" data-id="element-13301">
            45 000 ₽
          </div>
          <Button size="lg" onClick={() => showToast('Заявка на выплату создана')} data-id="element-13302">
            Запросить выплату
          </Button>
        </Card>

        <Card padding="lg" className="bg-slate-50 border-slate-200 flex flex-col justify-center" data-id="element-13303">
          <div className="space-y-4" data-id="element-13304">
            <div className="flex justify-between items-center border-b border-slate-200 pb-3" data-id="element-13305">
              <span className="text-slate-600" data-id="element-13306">В ожидании (Hold 14 дней)</span>
              <span className="font-bold text-slate-900" data-id="element-13307">3 920 ₽</span>
            </div>
            <div className="flex justify-between items-center border-b border-slate-200 pb-3" data-id="element-13308">
              <span className="text-slate-600" data-id="element-13309">Выплачено за все время</span>
              <span className="font-bold text-emerald-600" data-id="element-13310">2 600 000 ₽</span>
            </div>
            <div className="flex justify-between items-center" data-id="element-13311">
              <span className="text-slate-600" data-id="element-13312">Базовая комиссия платформы</span>
              <span className="font-bold text-slate-900" data-id="element-13313">15%</span>
            </div>
          </div>
        </Card>
      </div>

      <h3 className="text-lg font-bold text-slate-900 mt-8 mb-4" data-id="element-13314">
        История выплат
      </h3>
      <Card padding="none" className="overflow-hidden" data-id="element-13315">
        <table className="w-full text-left text-sm" data-id="element-13316">
          <thead className="bg-slate-50 text-slate-500 border-b border-slate-200" data-id="element-13317">
            <tr data-id="element-13318">
              <th className="px-4 py-3 font-medium" data-id="element-13319">Дата</th>
              <th className="px-4 py-3 font-medium" data-id="element-13320">Сумма</th>
              <th className="px-4 py-3 font-medium" data-id="element-13321">Статус</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-13322">
            {myPayouts.map(payout => <tr key={payout.id} className="hover:bg-slate-50" data-id="element-13323">
                <td className="px-4 py-3 text-slate-600" data-id="element-13324">{payout.createdAt}</td>
                <td className="px-4 py-3 font-bold text-slate-900" data-id="element-13325">
                  {payout.amount}
                </td>
                <td className="px-4 py-3" data-id="element-13326">
                  <Badge variant={payout.status === 'paid_out' ? 'success' : 'warning'} data-id="element-13327">
                    {payout.status === 'paid_out' ? 'Выплачено' : 'В обработке'}
                  </Badge>
                </td>
              </tr>)}
          </tbody>
        </table>
      </Card>
    </div>;
  return <div className="space-y-6" data-id="element-13328">
      {activeTab !== 'create' && renderTopStats()}

      <div className="pt-2" data-id="element-13329">
        {activeTab === 'products' && renderProducts()}
        {activeTab === 'create' && renderProductEditor()}
        {activeTab === 'sales' && renderSales()}
        {activeTab === 'balance' && renderBalance()}
      </div>
    </div>;
}